import React from "react";
import { Grid2, Typography } from "@mui/material";
import FormAutocomplete from "../../forms/FormAutocomplete";
import {
  Control,
  FieldError,
  useFieldArray,
  UseFormSetValue,
  UseFormTrigger,
  useWatch,
} from "react-hook-form";
import Tile from "../../../types/tile";
import EmailGroup from "../../../types/emailGroup";
import NotificationSettings from "../../../types/notificationSettings";
import FormMultiCheckbox from "../../forms/FormMultiCheckbox";
import { AlertDeliveryTypes } from "../../../types/alertDelivery";
import Option from "../../../types/option";
import { Applications } from "../../../types/company";
import { checkLicense } from "../../../helpers/checkLicense";
import { useSelector } from "../../../store";
import { wrapWithTooltip } from "../../../helpers/wrapWithTooltip";

interface Props {
  control: Control<Tile, any>;
  alertKey: "offline" | "threshold";
  notificationSettings?: NotificationSettings;
  emailGroups?: EmailGroup[];
  setValue: UseFormSetValue<Tile>;
  loadWithSkeleton: (
    element: React.ReactNode,
  ) =>
    | string
    | number
    | boolean
    | Iterable<React.ReactNode>
    | JSX.Element
    | null
    | undefined;
  responderIncidentTypes: Option[];
  gmdJobTypes: Option[];
  error?: FieldError;
  trigger?: UseFormTrigger<Tile>;
}

const AlertDeliveryForm: React.FC<Props> = (props) => {
  const {
    control,
    alertKey,
    notificationSettings,
    emailGroups,
    loadWithSkeleton,
    responderIncidentTypes,
    gmdJobTypes,
    error,
    trigger,
  } = props;

  const { fields, append, remove } = useFieldArray({
    control,
    name: `alerts.${alertKey}.alertDeliveries`,
  });

  const alertDeliveries = useWatch({
    control,
    name: `alerts.${alertKey}.alertDeliveries`,
  });

  const handleCheckboxChange = async (checked: boolean, id: number) => {
    const subType = id;
    const methodId =
      id === AlertDeliveryTypes.Email ||
      id === AlertDeliveryTypes.Site ||
      id === AlertDeliveryTypes.Text
        ? id
        : AlertDeliveryTypes.SvPro;

    const existingIndex = fields.findIndex(
      (f) =>
        f.deliveryMethod.id === methodId &&
        (f.methodSubtype === subType || !f.methodSubtype),
    );

    if (checked && existingIndex === -1) {
      append({
        id: 0,
        deliveryMethod: { id: methodId, value: AlertDeliveryTypes[methodId] },
        methodSubtype: subType,
        sent: false,
        gmdJobType: {} as Option,
        responderIncidentType: {} as Option,
        investigatorCaseType: {} as Option,
      });
    } else if (!checked && existingIndex !== -1) {
      remove(existingIndex);
    }

    await trigger?.(`alerts.${alertKey}.deliveryRequired`);
  };

  const mapOptions = (settingsTypes: Option[], fullTypes: Option[]) => {
    return settingsTypes.map((o) => {
      const it = fullTypes.find((i) => i.id === o.id);
      return {
        key: o.id,
        id: o.id,
        value: it?.value,
      };
    });
  };

  const getDeliveryKey = (
    prefix: string,
    delivery: any,
    fallbackIndex: number,
  ) => {
    const id = delivery?.id;
    const base =
      id !== undefined && id !== null
        ? String(id)
        : `${delivery?.deliveryMethod?.id ?? "unknown"}-${
            delivery?.methodSubtype ?? "none"
          }-${fallbackIndex}`;
    return `${prefix}-${base}`;
  };

  const { userCompany } = useSelector((store) => store.auth);

  return (
    <>
      <Grid2 container spacing={2} width={"100%"}>
        <Typography variant="caption" width="100%">
          How should these notifications be sent?
        </Typography>
        {/* If email notifications are allowed by company and there are saved email lists */}
        {!!notificationSettings?.emailNotifications && !!emailGroups && (
          <Grid2
            size={{ xs: 12, sm: 6, md: 4 }}
            display="flex"
            flexDirection="column"
            gap={1}
          >
            <Grid2 size={12}>
              {loadWithSkeleton(
                <FormMultiCheckbox
                  control={control}
                  fieldName={`alerts.${alertKey}.alertDeliveries.Email`}
                  label="Email & Text Message"
                  onChange={(e) =>
                    handleCheckboxChange(
                      e.target.checked,
                      AlertDeliveryTypes.Email,
                    )
                  }
                  checked={fields?.some(
                    (item) =>
                      item.deliveryMethod?.id === AlertDeliveryTypes.Email,
                  )}
                />,
              )}
            </Grid2>
            {/* Email Group Selection */}
            <Grid2 size={12}>
              {!!emailGroups &&
                fields.map((delivery, index) => {
                  if (delivery.deliveryMethod.id !== AlertDeliveryTypes.Email) {
                    return null;
                  }

                  return (
                    <React.Fragment
                      key={getDeliveryKey("alert-email", delivery, index)}
                    >
                      {loadWithSkeleton(
                        <FormAutocomplete
                          control={control}
                          fieldName={`alerts.${alertKey}.alertDeliveries.${index}.emailGroups`}
                          label="Notification Groups"
                          required
                          options={emailGroups || []}
                          multiple
                        />,
                      )}
                    </React.Fragment>
                  );
                })}
            </Grid2>
          </Grid2>
        )}

        {/* If SV notifications are allowed by company */}
        {!!notificationSettings?.svNotifications && (
          <>
            {!!notificationSettings?.enableGmd &&
              checkLicense(Applications.Gmd, userCompany?.licenses || []) && (
                <Grid2
                  size={{ xs: 12, sm: 6, md: 4 }}
                  display="flex"
                  flexDirection="column"
                  gap={1}
                >
                  {wrapWithTooltip(
                    notificationSettings?.gmdTicketTypes?.length! < 1,
                    "Add GMD Job Types in the Notifications settings to enable this feature.",
                    <Grid2 size={12}>
                      {loadWithSkeleton(
                        <FormMultiCheckbox
                          control={control}
                          fieldName={`alerts.${alertKey}.alertDeliveries.SvGmdJob`}
                          label="SV Getting More Done Job"
                          onChange={(e) =>
                            handleCheckboxChange(
                              e.target.checked,
                              AlertDeliveryTypes.Gmd,
                            )
                          }
                          checked={alertDeliveries?.some(
                            (item) =>
                              item.deliveryMethod?.id ===
                                AlertDeliveryTypes.SvPro &&
                              item.methodSubtype === AlertDeliveryTypes.Gmd,
                          )}
                          disabled={
                            !notificationSettings.gmdTicketTypes ||
                            notificationSettings.gmdTicketTypes?.length < 1
                          }
                        />,
                      )}
                    </Grid2>,
                  )}
                  <Grid2 size={12}>
                    {fields.map((delivery, index) => {
                      if (
                        !(
                          delivery.deliveryMethod.id ===
                            AlertDeliveryTypes.SvPro &&
                          (delivery.methodSubtype === AlertDeliveryTypes.Gmd ||
                            !!delivery.gmdJobType?.id)
                        )
                      ) {
                        return null;
                      }

                      return (
                        <React.Fragment
                          key={getDeliveryKey("alert-gmd", delivery, index)}
                        >
                          {loadWithSkeleton(
                            <FormAutocomplete
                              options={mapOptions(
                                notificationSettings?.gmdTicketTypes || [],
                                gmdJobTypes,
                              )}
                              control={control}
                              fieldName={`alerts.${alertKey}.alertDeliveries.${index}.gmdJobType`}
                              label="GMD Job Type"
                              required
                            />,
                          )}
                        </React.Fragment>
                      );
                    })}
                  </Grid2>
                </Grid2>
              )}
            {!!notificationSettings?.enableResponder &&
              checkLicense(
                Applications.Responder,
                userCompany?.licenses || [],
              ) && (
                <Grid2
                  size={{ xs: 12, sm: 6, md: 4 }}
                  display="flex"
                  flexDirection="column"
                  gap={1}
                >
                  {wrapWithTooltip(
                    notificationSettings?.responderTicketTypes?.length! < 1,
                    "Add Responder Incident Types in the Notifications settings to enable this feature.",
                    <Grid2 size={12}>
                      {loadWithSkeleton(
                        <FormMultiCheckbox
                          control={control}
                          fieldName={`alerts.${alertKey}.alertDeliveries.SvResponderIncident`}
                          label="SV Responder Incident"
                          onChange={(e) =>
                            handleCheckboxChange(
                              e.target.checked,
                              AlertDeliveryTypes.Responder,
                            )
                          }
                          checked={alertDeliveries?.some(
                            (item) =>
                              item.deliveryMethod?.id ===
                                AlertDeliveryTypes.SvPro &&
                              item.methodSubtype ===
                                AlertDeliveryTypes.Responder,
                          )}
                          disabled={
                            !notificationSettings.responderTicketTypes ||
                            notificationSettings.responderTicketTypes?.length <
                              1
                          }
                        />,
                      )}
                    </Grid2>,
                  )}
                  <Grid2 size={12}>
                    {fields.map((delivery, index) => {
                      if (
                        !(
                          delivery.deliveryMethod.id ===
                            AlertDeliveryTypes.SvPro &&
                          (delivery.methodSubtype ===
                            AlertDeliveryTypes.Responder ||
                            !!delivery.responderIncidentType?.id)
                        )
                      ) {
                        return null;
                      }

                      return (
                        <React.Fragment
                          key={getDeliveryKey(
                            "alert-responder",
                            delivery,
                            index,
                          )}
                        >
                          {loadWithSkeleton(
                            <FormAutocomplete
                              options={mapOptions(
                                notificationSettings?.responderTicketTypes ||
                                  [],
                                responderIncidentTypes,
                              )}
                              control={control}
                              fieldName={`alerts.${alertKey}.alertDeliveries.${index}.responderIncidentType`}
                              label="Responder Incident Type"
                              required
                            />,
                          )}
                        </React.Fragment>
                      );
                    })}
                  </Grid2>
                </Grid2>
              )}
            {!!notificationSettings?.enableInvestigator &&
              checkLicense(
                Applications.Investigator,
                userCompany?.licenses || [],
              ) && (
                <Grid2
                  size={{ xs: 12, sm: 6, md: 4 }}
                  display="flex"
                  flexDirection="column"
                  gap={1}
                >
                  <Grid2 size={12}>
                    {loadWithSkeleton(
                      <FormMultiCheckbox
                        control={control}
                        fieldName={`alerts.${alertKey}.alertDeliveries.SvInvestigatorCase`}
                        label="SV Investigator Case"
                        onChange={(e) =>
                          handleCheckboxChange(
                            e.target.checked,
                            AlertDeliveryTypes.Investigator,
                          )
                        }
                        checked={alertDeliveries?.some(
                          (item) =>
                            item.deliveryMethod?.id ===
                              AlertDeliveryTypes.SvPro &&
                            item.methodSubtype ===
                              AlertDeliveryTypes.Investigator,
                        )}
                        disabled={
                          !notificationSettings.investigatorTicketTypes ||
                          notificationSettings.investigatorTicketTypes?.length <
                            1
                        }
                      />,
                    )}
                  </Grid2>
                  <Grid2 size={12}>
                    {fields.map((delivery, index) => {
                      if (
                        !(
                          delivery.deliveryMethod.id ===
                            AlertDeliveryTypes.Investigator &&
                          (delivery.methodSubtype ===
                            AlertDeliveryTypes.Responder ||
                            !!delivery.investigatorCaseType?.id)
                        )
                      ) {
                        return null;
                      }

                      return (
                        <React.Fragment
                          key={getDeliveryKey(
                            "alert-investigator",
                            delivery,
                            index,
                          )}
                        >
                          {loadWithSkeleton(
                            <FormAutocomplete
                              options={[{ id: 1, value: "Service Ticket" }]}
                              control={control}
                              fieldName={`alerts.${alertKey}.alertDeliveries.${index}.investigatorCaseType`}
                              label="Investigator Case Type"
                              required
                            />,
                          )}
                        </React.Fragment>
                      );
                    })}
                  </Grid2>
                </Grid2>
              )}
          </>
        )}

        {/* If site notifications are allowed by company */}
        {!!notificationSettings?.siteNotifications && (
          <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
            {loadWithSkeleton(
              <FormMultiCheckbox
                control={control}
                fieldName={`alerts.${alertKey}.alertDeliveries.PushNotification`}
                label="Site Notification"
                onChange={(e) =>
                  handleCheckboxChange(
                    e.target.checked,
                    AlertDeliveryTypes.Site,
                  )
                }
                checked={fields?.some(
                  (item) => item.deliveryMethod?.id === AlertDeliveryTypes.Site,
                )}
              />,
            )}
          </Grid2>
        )}
      </Grid2>
      <Typography variant="caption" color="error">
        {error?.message}
      </Typography>

      {/* SvProTicket Deliveries with Unique Subtypes */}
      {/* {fields.map((delivery, index) =>
        delivery.deliveryMethod.id === AlertDeliveryTypes.SvPro ? (
          <React.Fragment key={index}>
            {(delivery.methodSubtype === AlertDeliveryTypes.Gmd ||
              !!delivery.gmdJobType?.id) && (
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                {loadWithSkeleton(
                  <FormAutocomplete
                    options={mapOptions(
                      notificationSettings?.gmdTicketTypes || [],
                      gmdJobTypes
                    )}
                    control={control}
                    fieldName={`alerts.${alertKey}.alertDeliveries.${index}.gmdJobType`}
                    label="GMD Job Type"
                    required
                  />
                )}
              </Grid2>
            )}
            {(delivery.methodSubtype === AlertDeliveryTypes.Responder ||
              !!delivery.responderIncidentType?.id) && (
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                {loadWithSkeleton(
                  <FormAutocomplete
                    options={mapOptions(
                      notificationSettings?.responderTicketTypes || [],
                      responderIncidentTypes
                    )}
                    control={control}
                    fieldName={`alerts.${alertKey}.alertDeliveries.${index}.responderIncidentType`}
                    label="Responder Incident Type"
                    required
                  />
                )}
              </Grid2>
            )}
            {(delivery.methodSubtype === AlertDeliveryTypes.Investigator ||
              !!delivery.investigatorCaseType?.id) && (
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                {loadWithSkeleton(
                  <FormAutocomplete
                    options={[{ id: 1, value: "Service Ticket" }]}
                    control={control}
                    fieldName={`alerts.${alertKey}.alertDeliveries.${index}.investigatorCaseType`}
                    label="Investigator Case Type"
                    required
                  />
                )}
              </Grid2>
            )}
          </React.Fragment>
        ) : null
      )} */}
    </>
  );
};

export default AlertDeliveryForm;
